import { Object3D } from "three";
import { type GLTF } from "three/addons/loaders/GLTFLoader.js";
import { createGltfLoader } from "./gltfLoaderFactory";
import { clone } from "three/addons/utils/SkeletonUtils.js";
import { Ioc } from "@core/ioc/ioc";
import {
  type AssetDto,
  assetObjectKey,
} from "@models/data/assetDto";
import type { IStorageModel, TransferProgress } from "@models/storage/IStorageModel";
import {
  dropCachedObjectBytes,
  readCachedObjectBytes,
  writeCachedObjectBytes,
} from "@models/storage/projectBinaryCache";
import { STORAGE_MODEL } from "@models/storage/IStorageModel";
import { runWorkflowPerformanceOperationAsync } from "@models/project/SceneNavigationDiagnosticLogger";
import type { ThreeEngine } from "@runtime/engine/engineService";
import { enforceTextureBudgetOnGltf } from "./textureBudget";
import { normalizeGltfToUnityLayout } from "./gltfUnityLayout";
import {
  parseGltfResourceBundleAsync,
  readGltfZipBundleAsync,
} from "./gltfResourceBundle";

export interface ThreeGltfAsset {
  readonly gltf: GLTF;
  instantiateScene(): Object3D;
}

/**
 * Give a parsed asset the desktop client's node tree — glTFast's primitive
 * layout and Unity's scene-layer strip — before anything walks it by
 * `HierachyID`. Idempotent; a node it cannot reshape is reported once and
 * left as the loader built it, which is the pre-2026-09-11 behaviour.
 */
function applyUnityLayout(gltf: GLTF, label: string): void {
  const report = normalizeGltfToUnityLayout(gltf);
  if (report.skipped.length > 0) {
    console.warn(
      `[GltfAssetCache] ${label}: ${report.skipped.length} multi-primitive node(s) kept the three.js layout — `
      + report.skipped.slice(0, 5).join("; "),
    );
  }
}

/**
 * Per-customer decoded-GLTF cache.
 *
 * Lifetime: ONE instance per signed-in customer for the viewport's lifetime —
 * `ViewportView.bootstrapRendering` keeps it across project switches and only
 * rebuilds on a genuine customer change (task 0012). Its CONTENT is cleared
 * explicitly on project unload (`SceneEntityRenderer`'s
 * `onActiveProjectChanged(null)` handler), which is the deliberate
 * memory-bound choice: decoded scenes are large, and an instance that
 * accumulated every asset ever viewed would grow without bound over a long
 * session. The cost is that two projects sharing a library asset re-download
 * it on switch; correctness never depended on the clear (asset ids are
 * account-scoped).
 *
 * `clear()` also ABORTS every in-flight S3 transfer rather than orphaning it
 * — a discarded download would otherwise keep competing for bandwidth with
 * its own replacement.
 */
export class GltfAssetCache {
  private readonly m_loader = createGltfLoader();
  private readonly m_customerId: string;
  private readonly m_byAsset = new Map<string, ThreeGltfAsset>();
  /**
   * Bumped whenever an asset is DECODED into the cache.
   *
   * A model's animation takes are only knowable once its glTF is decoded, and
   * nothing else announces that moment: a scene-load session covers a scene
   * switch, but placing a model into an ALREADY-loaded scene opens none. Any
   * consumer caching an answer derived from decoded content keys on this.
   */
  private m_decodeGeneration = 0;
  private readonly m_scaleByAsset = new Map<string, number>();
  private readonly m_importerByAsset = new Map<string, ReadonlyArray<number>>();
  private readonly m_inFlight = new Map<string, Promise<ThreeGltfAsset | null>>();
  /** Download-progress subscribers per in-flight asset — see {@link getOrLoad}. */
  private readonly m_progressListeners = new Map<string, Set<TransferProgress>>();
  /** Last report per in-flight asset, replayed to a late subscriber. */
  private readonly m_lastProgress = new Map<string, readonly [number, number | null]>();
  /** Owns every in-flight transfer; replaced (not reused) on `clear()`. */
  private m_abort = new AbortController();

  constructor(customerId: string, _engine?: ThreeEngine) {
    this.m_customerId = customerId;
  }

  /** See {@link m_decodeGeneration}. */
  get decodeGeneration(): number {
    return this.m_decodeGeneration;
  }

  get customerId(): string {
    return this.m_customerId;
  }

  has(assetId: string): boolean {
    return this.m_byAsset.has(assetId);
  }

  get(assetId: string): ThreeGltfAsset | null {
    return this.m_byAsset.get(assetId) ?? null;
  }

  getScale(assetId: string): number {
    return this.m_scaleByAsset.get(assetId) ?? 1;
  }

  getImporterVersion(assetId: string): ReadonlyArray<number> {
    return this.m_importerByAsset.get(assetId) ?? [0, 0, 0];
  }

  /**
   * Inject an already-parsed GLTF (e.g. a freshly imported model the user
   * just picked) so {@link getOrLoad} short-circuits with zero S3 round-trip
   * and the entity renders on the next `onEntityChangesApplied` tick. The
   * import write path uploads the same GLB bytes to S3 in the background;
   * a later cold reload re-fetches through {@link loadAsync}.
   *
   * `gltf` is retained verbatim (its `scene` is `clone()`d per placement),
   * so callers must not mutate it after injection. No blob URL is tracked —
   * `GLTFLoader.parseAsync` decodes straight from the ArrayBuffer.
   */
  injectParsedGltf(
    assetId: string,
    gltf: GLTF,
    scale: number,
    importerVersion: ReadonlyArray<number>,
  ): ThreeGltfAsset {
    // The import path reshapes the graph before authoring its parts, so this
    // is normally a no-op; it is the belt for a caller that did not.
    applyUnityLayout(gltf, `injected:${assetId}`);
    // Same retention rule as `loadAsync`: the parser keeps the GLB body and
    // full-size decoded images alive, and nothing in `src/` reads it. The
    // import parse went through `createGltfLoader`, so its textures were
    // already capped at decode time by the budget plugin.
    (gltf as { parser?: unknown }).parser = undefined;
    const cached: ThreeGltfAsset = {
      gltf,
      instantiateScene: () => clone(gltf.scene),
    };
    this.m_byAsset.set(assetId, cached);
    this.m_decodeGeneration++;
    this.m_scaleByAsset.set(assetId, scale > 0 ? scale : 1);
    this.m_importerByAsset.set(assetId, importerVersion);
    return cached;
  }

  /** Drop a single cached asset (import rollback). Revokes nothing — no
   *  blob URL outlives its parse (see `parseGlbAsync`). */
  evict(assetId: string): void {
    this.m_byAsset.delete(assetId);
    this.m_scaleByAsset.delete(assetId);
    this.m_importerByAsset.delete(assetId);
    this.m_inFlight.delete(assetId);
    this.m_progressListeners.delete(assetId);
    this.m_lastProgress.delete(assetId);
  }

  /**
   * `onProgress` reports the S3 transfer byte-by-byte (see
   * {@link TransferProgress}); it stops at the last byte, because glTF parse
   * and Draco decode expose no progress of their own. It is NOT called for a
   * cache hit — the caller already has the asset and should treat that as
   * complete.
   *
   * De-duplication makes fan-out necessary rather than optional: a second
   * caller that joins an in-flight load would otherwise be handed a promise
   * and never hear a byte, so every subscriber is kept in
   * {@link m_progressListeners} and a late joiner is replayed the last report
   * immediately (a scene with the same asset placed twice must not show one
   * placement frozen at zero while the other animates).
   */
  getOrLoad(assetId: string, onProgress?: TransferProgress): Promise<ThreeGltfAsset | null> {
    if (!assetId) return Promise.resolve(null);
    const cached = this.m_byAsset.get(assetId);
    if (cached) return Promise.resolve(cached);
    const inFlight = this.m_inFlight.get(assetId);
    if (inFlight) {
      if (onProgress) this.subscribeProgress(assetId, onProgress);
      return inFlight;
    }

    if (onProgress) this.subscribeProgress(assetId, onProgress);
    const promise = this.loadAsync(assetId);
    this.m_inFlight.set(assetId, promise);
    // Guarded cleanup: `clear()` wipes the maps mid-flight, and a NEW load
    // for the same asset may have registered afterwards — this stale finally
    // must not evict the replacement's in-flight entry (breaking its dedup)
    // or its freshly subscribed progress listeners.
    promise.finally(() => {
      if (this.m_inFlight.get(assetId) !== promise) return;
      this.m_inFlight.delete(assetId);
      this.m_progressListeners.delete(assetId);
      this.m_lastProgress.delete(assetId);
    });
    return promise;
  }

  private subscribeProgress(assetId: string, onProgress: TransferProgress): void {
    let listeners = this.m_progressListeners.get(assetId);
    if (!listeners) {
      listeners = new Set();
      this.m_progressListeners.set(assetId, listeners);
    }
    listeners.add(onProgress);
    const last = this.m_lastProgress.get(assetId);
    if (last) onProgress(last[0], last[1]);
  }

  private emitProgress(assetId: string, loaded: number, total: number | null): void {
    this.m_lastProgress.set(assetId, [loaded, total]);
    const listeners = this.m_progressListeners.get(assetId);
    if (!listeners) return;
    for (const listener of listeners) listener(loaded, total);
  }

  private async loadAsync(assetId: string): Promise<ThreeGltfAsset | null> {
    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
    // Captured up front: `clear()` swaps the controller, and a load that
    // straddles a clear must keep observing the controller it started under.
    const signal = this.m_abort.signal;

    let assetDto: AssetDto | null;
    try {
      assetDto = await runWorkflowPerformanceOperationAsync(
        "asset metadata fetch",
        { assetId, customerId: this.m_customerId },
        () => storage.getAssetById(this.m_customerId, assetId, signal),
      );
    } catch (err) {
      if (signal.aborted) return null;
      console.warn(`[GltfAssetCache] getAssetById failed (${assetId}):`, err);
      return null;
    }
    if (!assetDto) {
      console.warn(`[GltfAssetCache] no AssetData row for ${assetId}`);
      return null;
    }

    const assetScale =
      typeof assetDto.Scale === "number" && Number.isFinite(assetDto.Scale) && assetDto.Scale > 0
        ? assetDto.Scale
        : 1;
    this.m_scaleByAsset.set(assetId, assetScale);
    this.m_importerByAsset.set(assetId, parseImporterVersion(assetDto.ImporterVersion));

    const objectKey = assetObjectKey(assetDto);
    const ext = extOf(objectKey).toLowerCase();
    const onProgress: TransferProgress = (loaded, total) =>
      this.emitProgress(assetId, loaded, total);
    let gltf: GLTF | null;
    if (ext === ".glb") {
      gltf = await this.loadGlbAsync(assetId, objectKey, storage, signal, onProgress);
    } else if (ext === ".gltfzip") {
      // Unity-uploaded text .gltf assets: DEFLATE zip of the .gltf + its
      // external buffers/textures at their relative-URI paths
      // (`ZipHelper.ZipFiles`; Format "GLTF").
      gltf = await this.loadGltfZipAsync(assetId, objectKey, storage, signal, onProgress);
    } else {
      // .fbx / .pcrootzip / .mp4 / .pxz / .rtpackage / .usd — Unity itself
      // renders a placeholder for non-native rows; warn-skip is parity or
      // better. Raw animated-FBX support is a possible FBXLoader follow-up.
      console.warn(
        `[GltfAssetCache] skipping asset ${assetId} (unsupported ext "${ext}", objectKey="${objectKey}")`,
      );
      return null;
    }
    if (!gltf) return null;

    // Reshape multi-primitive nodes into the glTFast layout the persisted
    // `HierachyID`s address (`gltfUnityLayout.ts`). Needs `gltf.parser`, so
    // it MUST precede the texture-budget belt that strips it.
    applyUnityLayout(gltf, `${assetId} (${objectKey})`);

    // Cap oversized textures (belt over the decode-time plugin — see
    // `textureBudget.ts`) and release the parse-time source state: the
    // parser retains the whole GLB binary chunk plus full-size decoded
    // images, and nothing downstream reads `gltf.parser`. Runs BEFORE the
    // asset is cached, so no placement can upload a full-size texture.
    await runWorkflowPerformanceOperationAsync(
      "asset texture budget enforcement",
      { assetId, objectKey },
      () => enforceTextureBudgetOnGltf(gltf),
    );

    const parsed = gltf;
    const cached: ThreeGltfAsset = {
      gltf: parsed,
      instantiateScene: () => clone(parsed.scene),
    };
    this.m_byAsset.set(assetId, cached);
    this.m_decodeGeneration++;
    return cached;
  }

  /**
   * Fetch an asset object's bytes, decode them with `parse`, and — ONLY on a
   * successful decode — hand them to the persistent byte cache, so a
   * truncated or corrupt download can never be the thing we keep.
   *
   * A cached hit reports NO transfer progress: there are no bytes to move over
   * the network, and `SceneEntityRenderer` settles the placement's download
   * share when the load returns either way (see the note on {@link getOrLoad}).
   *
   * Cached bytes that fail to decode are dropped and re-fetched once. That is
   * the one way a hit can be worse than a miss — a half-written entry from a
   * killed tab, or disk corruption — and without the retry the user would be
   * stuck with a broken model until they cleared the cache by hand.
   */
  private async withAssetBytes(
    assetId: string,
    objectKey: string,
    storage: IStorageModel,
    signal: AbortSignal,
    onProgress: TransferProgress,
    parse: (blob: Blob) => Promise<GLTF | null>,
  ): Promise<GLTF | null> {
    for (let attempt = 0; attempt < 2; attempt++) {
      const performanceDetails = {
        assetId,
        objectKey,
        attempt: attempt + 1,
      };
      const hit =
        attempt === 0
          ? await runWorkflowPerformanceOperationAsync(
            "asset byte cache read",
            performanceDetails,
            () => readCachedObjectBytes(this.m_customerId, objectKey, signal),
          )
          : null;
      const source =
        hit ??
        (await runWorkflowPerformanceOperationAsync(
          "asset S3 download",
          performanceDetails,
          () => this.downloadAssetBlobAsync(assetId, objectKey, storage, signal, onProgress),
        ));
      if (!source) return null;

      const gltf = await runWorkflowPerformanceOperationAsync(
        "asset glTF decode",
        { ...performanceDetails, cacheHit: hit !== null },
        () => parse(source.blob),
      );
      if (gltf) {
        if (!hit) {
          writeCachedObjectBytes(
            this.m_customerId,
            objectKey,
            source.blob,
            source.contentType,
          );
        }
        return gltf;
      }
      if (signal.aborted || !hit) return null;

      console.warn(
        `[GltfAssetCache] cached bytes for ${assetId} (${objectKey}) failed to decode — dropping the entry and re-fetching`,
      );
      await dropCachedObjectBytes(this.m_customerId, objectKey);
    }
    return null;
  }

  /**
   * The storage layer's contract is a blob URL the caller owns
   * (`IStorageModel.getAssetBlobUrl`), but the byte cache needs the Blob
   * itself. Reading it back through `fetch` takes a second reference to the
   * same immutable blob data rather than copying a few hundred megabytes, and
   * it keeps the storage interface out of this change — only this consumer
   * wants blobs, and caching the other two callers (the Pixyz conversion
   * output, the Save-to-Library source copy) would be pure quota waste, since
   * both are read exactly once.
   */
  private async downloadAssetBlobAsync(
    assetId: string,
    objectKey: string,
    storage: IStorageModel,
    signal: AbortSignal,
    onProgress: TransferProgress,
  ): Promise<{ blob: Blob; contentType: string | null } | null> {
    let got: { url: string; contentType: string | null } | null;
    try {
      got = await storage.getAssetBlobUrl(this.m_customerId, objectKey, signal, onProgress);
    } catch (err) {
      // An aborted transfer is `clear()` doing its job (project switch,
      // renderer teardown) — not worth a warning.
      if (signal.aborted) return null;
      console.warn(`[GltfAssetCache] S3 fetch failed for ${assetId} (${objectKey}):`, err);
      return null;
    }
    if (!got) {
      console.warn(`[GltfAssetCache] missing S3 object: ${objectKey}`);
      return null;
    }

    try {
      const blob = await (await fetch(got.url, { signal })).blob();
      return { blob, contentType: got.contentType };
    } catch (err) {
      if (!signal.aborted) {
        console.warn(`[GltfAssetCache] blob read failed for ${assetId} (${objectKey}):`, err);
      }
      return null;
    } finally {
      URL.revokeObjectURL(got.url);
    }
  }

  private loadGlbAsync(
    assetId: string,
    objectKey: string,
    storage: IStorageModel,
    signal: AbortSignal,
    onProgress: TransferProgress,
  ): Promise<GLTF | null> {
    return this.withAssetBytes(assetId, objectKey, storage, signal, onProgress, (blob) =>
      this.parseGlbAsync(assetId, objectKey, blob),
    );
  }

  private async parseGlbAsync(
    assetId: string,
    objectKey: string,
    blob: Blob,
  ): Promise<GLTF | null> {
    const url = URL.createObjectURL(blob);
    try {
      return await this.m_loader.loadAsync(url);
    } catch (err) {
      console.warn(`[GltfAssetCache] GLB load failed for ${assetId} (${objectKey}):`, err);
      return null;
    } finally {
      // `loadAsync` resolves only after the whole parse — including every
      // texture — so the URL (and therefore the multi-hundred-MB Blob behind
      // it) has nothing left to serve. Retaining it for the project's
      // lifetime was ~1× the file size of dead weight per asset.
      URL.revokeObjectURL(url);
    }
  }

  /**
   * `.gltfzip` read path (port of `GLTFastImporter.BuildFromGlb`'s unzip
   * leg). The shared bundle reader is also the loose-`.gltf` import parser,
   * so bytes accepted before upload cannot become unreadable after reload.
   * Missing images keep the historical transparent-placeholder behavior;
   * buffers remain required.
   */
  private loadGltfZipAsync(
    assetId: string,
    objectKey: string,
    storage: IStorageModel,
    signal: AbortSignal,
    onProgress: TransferProgress,
  ): Promise<GLTF | null> {
    return this.withAssetBytes(assetId, objectKey, storage, signal, onProgress, (blob) =>
      this.parseGltfZipAsync(assetId, objectKey, blob),
    );
  }

  private async parseGltfZipAsync(
    assetId: string,
    objectKey: string,
    blob: Blob,
  ): Promise<GLTF | null> {
    try {
      const bundle = await readGltfZipBundleAsync(blob);
      return await parseGltfResourceBundleAsync(bundle, {
        missingImages: "placeholder",
      });
    } catch (err) {
      console.warn(
        `[GltfAssetCache] .gltfzip load failed for ${assetId} (${objectKey}):`,
        err,
      );
      return null;
    }
  }

  /** @returns how many cached assets were dropped (for the settings readout). */
  clear(): number {
    const count = this.m_byAsset.size;
    this.m_byAsset.clear();
    this.m_scaleByAsset.clear();
    this.m_importerByAsset.clear();
    this.m_inFlight.clear();
    this.m_progressListeners.clear();
    this.m_lastProgress.clear();
    // Abort (not orphan) every in-flight S3 transfer — a discarded download
    // would keep pulling bytes and competing for bandwidth with whatever
    // replaces it. Fresh controller so loads issued after the clear proceed.
    this.m_abort.abort();
    this.m_abort = new AbortController();
    return count;
  }
}

function extOf(path: string): string {
  const dot = path.lastIndexOf(".");
  return dot < 0 ? "" : path.slice(dot);
}

function parseImporterVersion(s: string | undefined | null): ReadonlyArray<number> {
  if (!s) return [0, 0, 0];
  const parts = s.split(".").map((p) => Number.parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return [0, 0, 0];
  while (parts.length < 3) parts.push(0);
  return parts.slice(0, 3);
}

export const GLTF_ASSET_CACHE = Symbol.for("Campfire.GltfAssetCache");
